/**
 * OrgInlineDetection — directory-aware access-record check for /monitored-tenants.
 *
 * Mounted directly below the tenantId input in the Add Tenant form. As the
 * admin types a directory ID and pauses, this widget hits
 * `getOrganizationByTid` and surfaces one of four states:
 *
 *   - idle    : tid empty or shorter than a plausible GUID → render nothing
 *   - loading : request in flight → a small spinner and helper text
 *   - match   : an access record exists for the directory → a check and a
 *               line naming it (neutral: a pass state takes no hue)
 *   - missing : the directory is not on file → an info notification with
 *               the Quick-create action
 *
 * The quick-create modal is rendered by a sibling component that only mounts
 * when the admin actually opens it, so the full Organizations list query
 * (used to populate the parent-org dropdown and the slug-collision check) is
 * deferred until it's needed. Without this split, every "Add Tenant" click
 * would prefetch the entire org list.
 *
 * Guardian-voice notes:
 *   - "Microsoft directory" rather than "Entra tenant"
 *   - "access record" rather than "Organizations row"
 *   - Verdicts stay ≤6 words and named-subject when an org name is known
 */

import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/carbon/Button'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Spinner } from '@/components/carbon/Spinner'
import { IconCheck } from '@/components/carbon/icons'
import {
  api,
  type CreateOrganizationRequest,
  type OrganizationDetailDto,
  type OrganizationListItemDto,
} from '@/lib/api'
import { useOrganizations } from '@/hooks/useOrganizations'
import OrgFormModal from '@/components/OrganizationManagement/OrgFormModal'

/** Plausible GUID length — anything shorter is mid-typing, skip the round-trip. */
const MIN_TID_LENGTH = 32
/** Debounce window — long enough for typing pauses, short enough to feel live. */
const DEBOUNCE_MS = 300

type SlugAvailability = 'unknown' | 'checking' | 'available' | 'taken'

type LookupState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'match'; org: OrganizationDetailDto }
  | { kind: 'missing' }
  | { kind: 'error'; message: string }

interface Props {
  /** Current tenantId value from the parent form. Drives the lookup. */
  tid: string
  /** Optional context to seed the quick-create modal. */
  seedName?: string
  seedDomain?: string
  /** Notified after a successful quick-create. */
  onCreated?: (org: OrganizationDetailDto) => void
}

export default function OrgInlineDetection({
  tid,
  seedName,
  seedDomain,
  onCreated,
}: Props) {
  const [state, setState] = useState<LookupState>({ kind: 'idle' })
  const [modalOpen, setModalOpen] = useState(false)

  // Debounced lookup. `latestReq` guards against out-of-order responses when the
  // admin keeps typing — only the most recent fetch is allowed to update state.
  const latestReq = useRef(0)
  useEffect(() => {
    const trimmed = tid.trim()
    if (trimmed.length < MIN_TID_LENGTH) {
      setState({ kind: 'idle' })
      return
    }

    setState({ kind: 'loading' })
    const reqId = ++latestReq.current
    const timer = window.setTimeout(async () => {
      try {
        const result = await api.getOrganizationByTid(trimmed)
        if (reqId !== latestReq.current) return
        setState(result === null ? { kind: 'missing' } : { kind: 'match', org: result })
      } catch (err) {
        if (reqId !== latestReq.current) return
        const message = err instanceof Error ? err.message : 'Lookup failed'
        setState({ kind: 'error', message })
      }
    }, DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [tid])

  const handleCreated = (org: OrganizationDetailDto) => {
    setState({ kind: 'match', org })
    setModalOpen(false)
    onCreated?.(org)
  }

  if (state.kind === 'idle') return null

  return (
    <div className="mt-2">
      {state.kind === 'loading' && (
        <p className="m-0 flex items-center gap-2 text-helper text-text-helper" role="status" aria-live="polite">
          <Spinner size={16} />
          <span>Checking the access record.</span>
        </p>
      )}

      {state.kind === 'match' && (
        <p className="m-0 flex items-start gap-2 text-helper text-text-secondary" role="status" aria-live="polite">
          <IconCheck className="mt-px flex-none text-text-primary" />
          <span>
            Organization <b className="font-semibold text-text-primary">{state.org.name}</b> exists for this directory.
          </span>
        </p>
      )}

      {state.kind === 'missing' && (
        <InlineNotification
          kind="info"
          title="No access record exists for this directory."
          actions={
            <Button kind="tertiary" size="sm" onClick={() => setModalOpen(true)}>
              Quick-create access record
            </Button>
          }
        >
          Users from this company will see a &ldquo;not yet active&rdquo; page when they try to sign in.
        </InlineNotification>
      )}

      {state.kind === 'error' && (
        <InlineNotification kind="error" title="The access record check failed.">
          {state.message}
        </InlineNotification>
      )}

      {/* Modal child is only mounted when the admin opens it. Deferring the
          mount keeps useOrganizations (which fetches the entire org list)
          out of the hot path for the common case where the directory already
          has an access record. */}
      {modalOpen && (
        <QuickCreateModal
          tid={tid.trim()}
          seedName={seedName}
          seedDomain={seedDomain}
          onClose={() => setModalOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* QuickCreateModal — mounts on demand, owns the heavy org-list query.        */
/* -------------------------------------------------------------------------- */

interface QuickCreateProps {
  tid: string
  seedName?: string
  seedDomain?: string
  onClose: () => void
  onCreated: (org: OrganizationDetailDto) => void
}

function QuickCreateModal({
  tid,
  seedName,
  seedDomain,
  onClose,
  onCreated,
}: QuickCreateProps) {
  const orgs = useOrganizations({ pageSize: 200 })

  const items = orgs.data?.items ?? []
  const msps: OrganizationListItemDto[] = items.filter(
    o => o.type === 'MSP' && o.isActive,
  )

  const [slugAvailability, setSlugAvailability] = useState<SlugAvailability>('unknown')
  const handleSlugCheck = (slug: string) => {
    if (!slug) return
    const collision = items.some(o => o.slug === slug)
    setSlugAvailability(collision ? 'taken' : 'available')
  }

  const handleSave = async (
    data: CreateOrganizationRequest | Partial<CreateOrganizationRequest>,
  ) => {
    // The modal's onSave signature widens to Partial for edit-mode reuse;
    // quick-create is always a full create, so narrow at the boundary.
    const created = (await orgs.createOrganization(
      data as CreateOrganizationRequest,
    )) as OrganizationDetailDto
    onCreated(created)
  }

  return (
    <OrgFormModal
      open
      mode="create"
      seed={{
        azureAdTenantId: tid,
        name: seedName,
        domain: seedDomain,
        type: 'Customer',
      }}
      msps={msps}
      onSave={handleSave}
      onClose={onClose}
      isSaving={orgs.isCreating}
      onSlugCheck={handleSlugCheck}
      slugAvailability={slugAvailability}
    />
  )
}
